import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { GitCompare, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

const inputCls = 'w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors placeholder-slate-700 disabled:opacity-40';

interface WorldSummary {
  id: string;
  name: string;
  currencySymbol: string;
  status: string;
  currentWsf: number | null;
  revenueShare: string | null;
  exchangeRate: string | null;
}

interface WorldMetrics {
  totalTransactions: number;
  totalVolume: string;
  uniquePlayers: number;
  avgReward: string;
}

function MetricRow({ label, values }: { label: string; values: (string | number | null)[] }) {
  return (
    <tr className="border-b border-white/[0.04]">
      <td className="py-3 px-4 text-xs text-slate-500 font-medium w-48">{label}</td>
      {values.map((v, i) => (
        <td key={i} className="py-3 px-4 text-xs text-slate-200 text-right font-mono">
          {v ?? '—'}
        </td>
      ))}
      {/* fill remaining slots */}
      {Array.from({ length: 3 - values.length }).map((_, i) => (
        <td key={`empty-${i}`} className="py-3 px-4 text-xs text-slate-700 text-right">—</td>
      ))}
    </tr>
  );
}

function WorldColumn({ worldId }: { worldId: string }) {
  const { data: world, isLoading } = useQuery<WorldSummary>({
    queryKey: ['world', worldId],
    queryFn: () => api.get(`/worlds/${worldId}`).then((r) => r.data),
    enabled: !!worldId,
  });

  const { data: metrics, isLoading: mLoading } = useQuery<WorldMetrics>({
    queryKey: ['world-metrics', worldId],
    queryFn: () => api.get(`/analytics/worlds/${worldId}/metrics`).then((r) => r.data),
    enabled: !!worldId,
  });

  const { data: rate } = useQuery<{ ukcPerUnit: string }>({
    queryKey: ['exchange-rate', worldId],
    queryFn: () => api.get(`/central-bank/exchange-rates/${worldId}`).then((r) => r.data),
    enabled: !!worldId,
  });

  if (!worldId) return null;
  if (isLoading || mLoading) {
    return (
      <div className="flex items-center justify-center py-8 col-span-1">
        <Loader2 className="w-5 h-5 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-semibold text-white truncate">{world?.name ?? worldId}</p>
      <span className="text-[10px] text-slate-500">{world?.currencySymbol}</span>
    </div>
  );
}

export function WorldComparePage() {
  const { data: allWorlds = [] } = useQuery<WorldSummary[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data?.worlds ?? r.data ?? []),
  });

  const [slots, setSlots] = useState<[string, string, string]>(['', '', '']);

  const worldQueries = slots.map((id) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuery<WorldSummary>({
      queryKey: ['world', id],
      queryFn: () => api.get(`/worlds/${id}`).then((r) => r.data),
      enabled: !!id,
    })
  );

  const metricsQueries = slots.map((id) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuery<WorldMetrics>({
      queryKey: ['world-metrics', id],
      queryFn: () => api.get(`/analytics/worlds/${id}/metrics`).then((r) => r.data),
      enabled: !!id,
    })
  );

  const rateQueries = slots.map((id) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuery<{ ukcPerUnit: string }>({
      queryKey: ['exchange-rate', id],
      queryFn: () => api.get(`/central-bank/exchange-rates/${id}`).then((r) => r.data),
      enabled: !!id,
    })
  );

  const worlds  = worldQueries.map((q) => q.data);
  const metrics = metricsQueries.map((q) => q.data);
  const rates   = rateQueries.map((q) => q.data);
  const isAnyLoading = [...worldQueries, ...metricsQueries].some((q) => q.isLoading);
  const hasAny = slots.some(Boolean);

  function setSlot(index: number, value: string) {
    setSlots((prev) => {
      const next: [string, string, string] = [...prev] as [string, string, string];
      next[index] = value;
      return next;
    });
  }

  const fmt = (v: string | number | null | undefined) =>
    v == null ? null : typeof v === 'number' ? v.toLocaleString() : v;

  return (
    <div className="p-8">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
          <GitCompare className="w-4 h-4 text-emerald-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">World Comparison</h2>
          <p className="text-sm text-slate-600 mt-0.5">Compare up to 3 worlds side by side</p>
        </div>
      </div>

      {/* World selectors */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {([0, 1, 2] as const).map((i) => (
          <div key={i}>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1.5">
              World {i + 1}
            </label>
            <select
              className={inputCls}
              value={slots[i]}
              onChange={(e) => setSlot(i, e.target.value)}
            >
              <option value="">— select world —</option>
              {allWorlds.map((w) => (
                <option key={w.id} value={w.id} disabled={slots.includes(w.id) && slots[i] !== w.id}>
                  {w.name} ({w.currencySymbol})
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {isAnyLoading && (
        <div className="flex items-center gap-2 text-slate-500 text-xs py-3">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading world data…
        </div>
      )}

      {hasAny && !isAnyLoading && (
        <motion.div
          className="card overflow-hidden"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Column headers */}
          <div className="grid grid-cols-4 border-b border-white/[0.06] bg-white/[0.02]">
            <div className="px-4 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">
              Metric
            </div>
            {slots.map((id, i) => (
              <div key={i} className="px-4 py-3 text-right">
                {id ? (
                  <div>
                    <p className="text-xs font-semibold text-white truncate">{worlds[i]?.name ?? '…'}</p>
                    <p className="text-[10px] text-slate-500">{worlds[i]?.currencySymbol}</p>
                  </div>
                ) : (
                  <span className="text-xs text-slate-700">—</span>
                )}
              </div>
            ))}
          </div>

          <table className="w-full">
            <tbody>
              <MetricRow label="Status"          values={slots.map((id, i) => id ? (worlds[i]?.status ?? '—') : null)} />
              <MetricRow label="WSF Factor"      values={slots.map((id, i) => id ? (worlds[i]?.currentWsf ?? '—') : null)} />
              <MetricRow label="Revenue Share %"  values={slots.map((id, i) => id ? (worlds[i]?.revenueShare ? `${worlds[i]!.revenueShare}%` : '—') : null)} />
              <MetricRow label="UKC per Unit"    values={slots.map((id, i) => id ? (rates[i]?.ukcPerUnit ? `${rates[i]!.ukcPerUnit} UKC` : '—') : null)} />
              <MetricRow label="Total Transactions" values={slots.map((id, i) => id ? fmt(metrics[i]?.totalTransactions ?? null) : null)} />
              <MetricRow label="Total Volume"    values={slots.map((id, i) => id ? fmt(metrics[i]?.totalVolume ?? null) : null)} />
              <MetricRow label="Unique Players"  values={slots.map((id, i) => id ? fmt(metrics[i]?.uniquePlayers ?? null) : null)} />
              <MetricRow label="Avg Reward"      values={slots.map((id, i) => id ? fmt(metrics[i]?.avgReward ?? null) : null)} />
            </tbody>
          </table>
        </motion.div>
      )}

      {!hasAny && (
        <div className="card p-12 text-center">
          <GitCompare className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-600 text-sm">Select at least one world to begin comparing.</p>
        </div>
      )}
    </div>
  );
}
